import { BLOG_CATEGORY_FILTERS } from './blog';

export { BLOG_CATEGORY_FILTERS };
export type {
  BlogCategoryId,
  BlogTagDto,
  BlogPostDto,
  BlogPostPayload,
  BlogListResult,
  BlogListPagination,
  BlogListCategoryCounts,
} from './blog';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  phoneNumber?: string;
  preferedTheme?: 'neutral' | 'purple' | 'pink' | 'baby-blue';
  preferredServiceAddress?: ServiceAddress;
  isEmailVerified: boolean;
  canPerformServices?: boolean;
  birthMonth?: number;
  birthDay?: number;
  /** Present when the account is linked to Google Sign-In (users.google_id). */
  googleId?: string | null;
  roles: UserRole[];
  createdAt: string;
  updatedAt: string;
}

export interface UserRole {
  id: string;
  userId: string;
  businessId?: string;
  role: string; // Now dynamic based on business_roles table
  permissions: string[]; // Array of permission names
  isActive: boolean;
  businessRoleActive?: boolean; // Whether the business role is active
  createdAt: string;
  businessName?: string;
  businessShortName?: string;
  roleDescription?: string;
  /** Per-business: access to business dashboard Customers page. Business owners always have access. */
  canAccessCustomers?: boolean;
  /** Per-business: view full customer phone numbers. Business owners always have access. Defaults to true. */
  canViewFullPhoneNumber?: boolean;
  dashboardType?: 'business' | 'professional' | 'customer'; // Which dashboard this role should use
  user?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface BusinessRole {
  id: string;
  businessId: string;
  name: string;
  description: string;
  permissions: string[];
  isActive: boolean;
  dashboardType?: 'business' | 'professional' | 'customer';
  createdAt: string;
  updatedAt: string;
}

export interface Business {
  id: string;
  name: string;
  shortName: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
  showContactForm?: boolean;
  websiteRedirectEnabled?: boolean;
  serviceLocation?: 'IN_STORE' | 'ON_SITE' | 'BOTH'; // Location where business services are performed
  categories?: string[]; // Array of business category IDs
  subCategories?: string[]; // Array of business subcategory IDs
  timezone?: string;
  operatingHours?: OperatingHours[];
  /**
   * True once the Guided Setup has been shown at least once to the business owner.
   * Used to only show the wizard on the first login.
   */
  guidedSetupShown?: boolean;
  askAiEnabled?: boolean;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface AskAiTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface AskAiContactDraft {
  wanted?: boolean;
  name?: string;
  email?: string;
  message?: string;
}

export interface AskAiAction {
  label: string;
  url?: string;
}

export interface AskAiSuggestion {
  short_name: string;
  name: string;
  category?: string;
  reason?: string;
  path?: string;
  primary?: AskAiAction;
  actions?: AskAiAction[];
}

export interface AskAiBookingService {
  id: string;
  name: string;
  durationMinutes: number;
  price?: number | null;
  category?: string;
  isAddOn?: boolean;
  quotable?: boolean;
  professionalName?: string;
  serviceLocation?: string;
}

export interface AskAiBookingServiceGroup {
  category: string;
  services: AskAiBookingService[];
}

export interface AskAiBookingTechnicianGroup {
  serviceId: string;
  serviceName: string;
  professionals: Array<{ id: string; name: string }>;
  selectedId?: string;
}

export interface ServiceAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
  locationNotes?: string;
  locationLabel?: string;
}

export interface AskAiBookingDraft {
  wanted?: boolean;
  serviceNames?: string[];
  serviceIds?: string[];
  candidateServiceNames?: string[];
  date?: string;
  time?: string;
  notes?: string;
  professionalId?: string;
  professionalName?: string;
  anyProfessional?: boolean;
  sameProfessional?: boolean;
  serviceProfessionals?: Array<{ serviceId: string; professionalId: string; professionalName?: string }>;
  replaceServices?: boolean;
  revisingServices?: boolean;
  quoteDetails?: string;
  preferredSchedule?: string;
  skipPreferredSchedule?: boolean;
  resetQuoteDetails?: boolean;
  resetPreferredSchedule?: boolean;
  resetProfessional?: boolean;
  quoteAttachmentIds?: string[];
  quoteImageUrls?: string[];
  recurring?: boolean;
  intervalWeeks?: number;
  occurrenceCount?: number;
  resetRecurring?: boolean;
  resetIntervalWeeks?: boolean;
  resetOccurrenceCount?: boolean;
  serviceAddress?: ServiceAddress;
  rememberServiceAddress?: boolean;
  resetServiceAddress?: boolean;
  useSavedAddress?: boolean;
  needsAddress?: boolean;
  customerId?: string;
  customerName?: string;
  customerQuery?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerEmail?: string;
  customerPhone?: string;
  newCustomer?: boolean;
  emailMatchConfirmed?: boolean;
  customerChoices?: Array<{ id: string; label: string }>;
  privateNotes?: string;
}

export interface AskAiBookingStatus {
  wanted?: boolean;
  needsLogin?: boolean;
  needsPhone?: boolean;
  ready?: boolean;
  booked?: boolean;
  missing?: string[];
  services?: AskAiBookingService[];
  date?: string;
  time?: string;
  timeLabel?: string;
  availableTimes?: string[];
  chips?: string[];
  serviceGroups?: AskAiBookingServiceGroup[];
  candidateServiceNames?: string[];
  technicianGroups?: AskAiBookingTechnicianGroup[];
  dateOptions?: Array<{ label: string; date: string }>;
  showCalendar?: boolean;
  depositRequired?: boolean;
  depositAmount?: number;
  depositDueLabel?: string;
  depositNonRefundable?: boolean;
  needsAddress?: boolean;
  serviceAddress?: ServiceAddress;
  rememberServiceAddress?: boolean;
  useBookingPage?: boolean;
  loginUrl?: string;
  phoneUrl?: string;
  bookingUrl?: string;
  depositUrl?: string;
  appointmentUrl?: string;
  summary?: string;
  businessId?: string;
  scheduledAt?: string;
  assignments?: Array<{ serviceId: string; durationMinutes: number; professionalId?: string }>;
  notes?: string;
  appointmentId?: string;
  professionalId?: string;
  professionalName?: string;
  anyProfessional?: boolean;
  sameProfessional?: boolean;
  serviceProfessionals?: Array<{ serviceId: string; professionalId: string; professionalName?: string }>;
  revising?: boolean;
  revisingServices?: boolean;
  totalPrice?: number;
  quote?: boolean;
  quoted?: boolean;
  quoteDetails?: string;
  preferredSchedule?: string;
  skipPreferredSchedule?: boolean;
  quoteInstructions?: Array<{ serviceName: string; instructions: string }>;
  quoteAttachmentIds?: string[];
  quoteImageUrls?: string[];
  quoteId?: string;
  quoteUrl?: string;
  recurring?: boolean;
  intervalWeeks?: number;
  occurrenceCount?: number;
  recurringOccurrences?: Array<{
    occurrenceIndex: number;
    scheduledAt: string;
    label: string;
    status: 'AVAILABLE' | 'CONFLICT';
    reason?: string;
  }>;
  recurringConflicts?: number;
  customerId?: string;
  customerName?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerEmail?: string;
  customerPhone?: string;
  newCustomer?: boolean;
  emailMatchConfirmed?: boolean;
  customerChoices?: Array<{ id: string; label: string }>;
  privateNotes?: string;
  allowOverride?: boolean;
  outsideOpenings?: boolean;
}

export interface AskAiCancelDraft {
  wanted?: boolean;
  appointmentId?: string;
  reason?: string;
  skipReason?: boolean;
  scope?: 'THIS' | 'REMAINING';
}

export interface AskAiCancelStatus {
  wanted?: boolean;
  appointmentId?: string;
  summary?: string;
  reason?: string;
  skipReason?: boolean;
  scope?: 'THIS' | 'REMAINING';
  needsScope?: boolean;
  remainingAfterThis?: number;
  ready?: boolean;
  cancelled?: boolean;
  picks?: Array<{ label: string; appointmentId: string }>;
  chips?: string[];
  policyNote?: string;
  appointmentUrl?: string;
  bookingUrl?: string;
}

export interface AskAiRescheduleDraft {
  wanted?: boolean;
  appointmentId?: string;
  date?: string;
  time?: string;
  resetDate?: boolean;
  resetTime?: boolean;
}

export interface AskAiRescheduleStatus {
  wanted?: boolean;
  appointmentId?: string;
  summary?: string;
  currentWhenLabel?: string;
  date?: string;
  time?: string;
  timeLabel?: string;
  newWhenLabel?: string;
  scheduledAt?: string;
  availableTimes?: string[];
  dateOptions?: Array<{ label: string; date: string }>;
  showCalendar?: boolean;
  chips?: string[];
  picks?: Array<{ label: string; appointmentId: string }>;
  missing?: string[];
  ready?: boolean;
  rescheduled?: boolean;
  cannotSelfReschedule?: boolean;
  policyNote?: string;
  appointmentUrl?: string;
  bookingUrl?: string;
  businessPhone?: string;
}

export interface AskAiSignupDraft {
  wanted?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
  birthMonth?: number;
  birthDay?: number;
  skipBirthday?: boolean;
  acceptedAgreement?: boolean;
  confirm?: boolean;
}

export interface AskAiSignupStatus {
  wanted?: boolean;
  step?: 'name' | 'email' | 'phone' | 'password' | 'birthday' | 'agreement' | 'confirm' | 'created' | 'blocked';
  created?: boolean;
  alreadyLoggedIn?: boolean;
  emailTaken?: boolean;
  ready?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  hasPassword?: boolean;
  passwordError?: string;
  firstNameError?: string;
  lastNameError?: string;
  phoneError?: string;
  emailError?: string;
  birthMonth?: number;
  birthDay?: number;
  skipBirthday?: boolean;
  acceptedAgreement?: boolean;
  missing?: string[];
  chips?: string[];
  agreementUrl?: string;
  loginUrl?: string;
  verifyUrl?: string;
  formUrl?: string;
}

export interface AskAiReply {
  answer: string;
  suggestions?: AskAiSuggestion[];
  chips?: string[];
  cancel?: AskAiCancelStatus;
  reschedule?: AskAiRescheduleStatus;
  contact?: {
    wanted?: boolean;
    sent?: boolean;
    missing?: string[];
    name?: string;
    email?: string;
    message?: string;
  };
  booking?: AskAiBookingStatus;
}

export interface OperatingHours {
  id: string;
  businessId: string;
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

export interface OperatingHoursFormData {
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}



export type { Service } from './service';

export interface Appointment {
  id: string;
  businessId: string;
  customerId: string;
  /** Specific time (ISO); null when appointment has a flexible window (flexScheduledAt). */
  scheduledAt: string | null;
  /** When set, appointment has a time window instead of a specific time (scheduledAt is null). */
  flexScheduledAt?: { earliest: string; latest: string } | null;
  status: 'PENDING' | 'PENDING_QUOTE' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  notes?: string;
  /** When set, this appointment is part of a recurring series. */
  seriesId?: string | null;
  /** 1..10 within the recurring series (if seriesId is set). */
  occurrenceIndex?: number | null;
  /** Weekly interval for the series (1..8), if known. */
  seriesIntervalWeeks?: number | null;
  /** Total number of occurrences in the series (1..10), if known. */
  seriesOccurrenceCount?: number | null;
  /** ScheduledAt of the next non-cancelled occurrence in the series (if known). */
  nextInSeriesScheduledAt?: string | null;
  /**
   * Who initiated the booking.
   * - CUSTOMER: customer self-booked
   * - BUSINESS: business owner/professional booked on behalf of customer (call-in)
   */
  bookingInitiator?: 'CUSTOMER' | 'BUSINESS';
  /**
   * Who initiated the most recent reschedule (if any).
   */
  rescheduleInitiator?: 'CUSTOMER' | 'BUSINESS' | null;
  /**
   * Who initiated the cancellation (if cancelled).
   */
  cancelInitiator?: 'CUSTOMER' | 'BUSINESS' | null;
  /** Authenticated user who created the appointment record (audit). */
  createdByUserId?: string | null;
  /** First name of the user who created the appointment */
  createdByFirstName?: string | null;
  /** Last name of the user who created the appointment */
  createdByLastName?: string | null;
  cancellationNotes?: string;
  noShow?: boolean;
  reminderSentAt?: string;
  createdAt: string;
  updatedAt: string;
  // Related data from joins
  business_name?: string;
  business_short_name?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  customer_phone?: string;
  /** When set, an invoice exists for this appointment (from booking). */
  invoiceId?: string | null;
  /** When set, this appointment was created from an accepted quote. */
  quoteId?: string | null;
  /** Final agreed amount from the linked quote (use for Total Est. when quoteId is set). */
  quoteFinalAgreedAmount?: number | null;
  /** Invoice status when invoiceId is set (e.g. DRAFT, SENT, PAID, VOIDED). */
  invoiceStatus?: string | null;
  /** Deposit required for this appointment (e.g. from quote). */
  requiredDepositAmount?: number | null;
  /** Deposit amount already paid by customer. */
  receivedDepositAmount?: number | null;
  /** When the deposit was paid (ISO string); set when receivedDepositAmount > 0. */
  depositPaidAt?: string | null;
  /** Series-aware required deposit total (when deposit applies to a recurring series). */
  effectiveRequiredDepositAmount?: number | null;
  /** Series-aware received deposit total. */
  effectiveReceivedDepositAmount?: number | null;
  /** True when a deposit is required and not yet fully received (series-aware). */
  isDepositOutstanding?: boolean;
  // Assignments data
  assignments: Array<{
    id: string;
    serviceId: string;
    serviceName: string;
    servicePrice: number;
    serviceIcon?: string;
    categoryBackgroundColor?: string | null;
    professionalId?: string;
    professionalFirstName?: string;
    professionalLastName?: string;
    professionalEmail?: string;
    durationMinutes: number;
    price: number;
    notes?: string;
    insistOnProfessional?: boolean;
    /**
     * UTC instants (ISO strings) computed in business timezone to respect DST boundaries.
     * Used for calendar rendering when appointments include multiple professionals.
     */
    fromDateTime?: string;
    toDateTime?: string;
    serviceAddress?: ServiceAddress | null;
    serviceLocation?: string | null;
  }>;
}

export type DepositPaymentDetailsSource = 'MANUAL' | 'ONLINE' | 'UNKNOWN';

export interface DepositPaymentDetails {
  source: DepositPaymentDetailsSource;
  amount: number;
  paidAt?: string | null;
  paymentMethod?: string | null;
  paymentDate?: string | null;
  referenceNumber?: string | null;
  notes?: string | null;
  recordedByFirstName?: string | null;
  recordedByLastName?: string | null;
  recordedAt?: string | null;
  onlineStatus?: string | null;
}

export interface AppointmentSeriesSummary {
  isRecurring: boolean;
  seriesId?: string;
  occurrenceIndex?: number | null;
  seriesIntervalWeeks?: number | null;
  seriesOccurrenceCount?: number | null;
  appointmentStatus?: string;
  completedCount?: number;
  cancelledCount?: number;
  totalCount?: number;
  remainingActiveIncludingThis?: number;
  remainingActiveAfterThis?: number;
  isLastActiveInstance?: boolean;
}

/** One row returned by GET /appointments/:id/series-appointments */
export interface AppointmentSeriesListItem {
  id: string;
  occurrenceIndex: number | null;
  scheduledAt: string | null;
  flexScheduledAt?: { earliest: string; latest: string } | null;
  status: string;
}

/** Single row from appointment_events (audit / activity timeline). */
export interface AppointmentEvent {
  id: string;
  appointmentId: string;
  eventType: string;
  /** JSON string: appointmentDateTime, services, and for EMAIL_SENT also emailType, to */
  eventText: string;
  createdBy: string | null;
  createdAt: string;
  createdByFirstName: string | null;
  createdByLastName: string | null;
}

/** Single row from invoice_events (audit / activity log). */
export interface InvoiceEvent {
  id: string;
  invoiceId: string;
  eventType: string;
  eventText: string;
  createdBy: string | null;
  createdAt: string;
  createdByFirstName: string | null;
  createdByLastName: string | null;
}

export interface AppointmentAssignment {
  serviceId: string;
  professionalId?: string;
  durationMinutes: number;
  notes?: string;
  insistOnProfessional?: boolean;
}

export interface CreateAppointmentData {
  businessId: string;
  customerId: string;
  scheduledAt: string;
  notes?: string;
  /**
   * Optional hint to help the server correctly classify booking initiator.
   * - customer: created from customer self-booking flow
   * - business: created from business/owner/professional booking flow
   */
  bookingContext?: 'customer' | 'business';
  /**
   * When true, allows active business users (owner/professional) to override conflict/time-off validations.
   * Customer booking flow should not set this.
   */
  allowOverride?: boolean;
  /**
   * Optional service address for ON_SITE services (applies to all ON_SITE assignments in the request).
   */
  serviceAddress?: ServiceAddress;
  /**
   * When true, saves the provided serviceAddress on the user for next time (if supported by DB).
   */
  rememberServiceAddress?: boolean;
  /**
   * Set when creating from the Ask AACore AI chatbox.
   */
  bookedViaAi?: boolean;
  aiChatSessionId?: string;
  assignments: AppointmentAssignment[];
}

export type RecurrenceMode = 'STRICT' | 'FLEXIBLE';

export interface CreateRecurringAppointmentsData {
  businessId: string;
  customerId: string;
  mode: RecurrenceMode;
  intervalWeeks: number; // 1..8
  occurrenceCount: number; // 1..10 (includes first)
  startDate: string; // YYYY-MM-DD (client timezone)
  startTime: string; // HH:mm (client timezone)
  clientTimeZone: string; // IANA
  notes?: string;
  serviceAddress?: ServiceAddress;
  rememberServiceAddress?: boolean;
  assignments: AppointmentAssignment[];
  bookedViaAi?: boolean;
  aiChatSessionId?: string;
}

export interface RecurringPreviewOccurrence {
  occurrenceIndex: number;
  scheduledAt: string; // ISO (UTC, Z)
  status: 'AVAILABLE' | 'CONFLICT';
  reason?: string;
  assignments?: Array<{
    serviceId: string;
    durationMinutes: number;
    professionalId: string | null;
    insistOnProfessional: boolean;
  }>;
}

export interface RecurringPreviewResponse {
  series: {
    mode: RecurrenceMode;
    intervalWeeks: number;
    occurrenceCount: number;
    clientTimeZone: string;
  };
  occurrences: RecurringPreviewOccurrence[];
}

export interface CreateRecurringAppointmentsResult {
  seriesId: string;
  appointmentIds: string[];
  initialStatus: 'PENDING' | 'PENDING_QUOTE' | 'CONFIRMED';
  firstAppointmentRequiredDepositAmount?: number | null;
}

export interface GiftCard {
  id: string;
  businessId: string | null; // null for universal gift cards
  issuedBy: string;
  issuedTo?: string;
  recipientEmail?: string;
  recipientName?: string;
  amount: number;
  balance: number;
  code: string;
  message?: string;
  status: 'ACTIVE' | 'REDEEMED' | 'EXPIRED' | 'CANCELLED';
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  // Related data
  business?: {
    name: string;
  } | null; // null for universal gift cards
  issuedByUser?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  issuedToUser?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface GiftCardTransaction {
  id: string;
  giftCardId: string;
  appointmentId?: string;
  type: 'ISSUED' | 'REDEEMED' | 'REFUNDED' | 'EXPIRED';
  amount: number;
  balanceAfter: number;
  notes?: string;
  processedBy?: string;
  createdAt: string;
  // Related data
  processedByUser?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  appointment?: {
    id: string;
    scheduledAt: string;
    service?: {
      name: string;
    };
  };
}

export interface GiftCardFormData {
  recipientEmail?: string;
  recipientName: string;
  amount: number;
  message?: string;
  expiresAt?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
  loginType?: 'customer' | 'business' | 'professional' | 'platform';
  /** Opaque browser trust token used to skip business 2FA when still valid */
  trustToken?: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  birthMonth?: number | string;
  birthDay?: number | string;
}

export interface AuthResponse {
  user: User;
  token: string;
  /** Returned after successful 2FA when "remember this browser" was checked */
  trustToken?: string;
}

export interface Login2faChallenge {
  requires2fa: true;
  challengeId: string;
  email: string;
  expiresInSeconds: number;
}

export type LoginApiResult = AuthResponse | Login2faChallenge;

export function isLogin2faChallenge(result: LoginApiResult): result is Login2faChallenge {
  return (result as Login2faChallenge).requires2fa === true;
}

export interface BusinessBranding {
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  textColor: string;
  backgroundColor: string;
  fontFamily: string;
  useCustomColors: boolean;
  layout: 'modern' | 'elegant' | 'minimal' | 'artistic' | 'neon' | 'vintage' | 'cosmic' | 'serene' | 'bold';
  themePreset?: string; // Theme preset key (e.g., 'ocean-pro', 'rose-studio', etc.)
  galleryImages?: string[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface QuoteConversationMessage {
  id: string;
  sender: 'business' | 'customer';
  senderId?: string;
  text: string;
  createdAt: string;
}

export interface QuoteEventRow {
  id: string;
  quoteId: string;
  eventType: string;
  eventText: string;
  createdBy?: string | null;
  createdAt: string;
  createdByFirstName?: string | null;
  createdByLastName?: string | null;
}

export interface QuoteRow {
  id: string;
  customerId: string;
  businessId: string;
  services: Array<{ id: string; price: number | null; name: string | null }>;
  preferredSchedule: string | null;
  offerSentAt?: string | null;
  offerExpirationDays?: number | null;
  offerExpiredAt?: string | null;
  customerProvidedDetails: string | null;
  customerUploadedImages?: string[] | null;
  businessOfferAmount: number | null;
  customerOfferAmount: number | null;
  finalAgreedAmount: number | null;
  requiredDepositAmount?: number | null;
  conversations?: QuoteConversationMessage[];
  quoteEvents?: QuoteEventRow[];
  status: string;
  createdAt: string;
  updatedAt: string;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  /** Present when listing quotes as a customer (per-business name). */
  businessName?: string | null;
  /** Business short name for linking to the business public page. */
  businessShortName?: string | null;
  /** When set, an appointment was created from this accepted quote (business detail view). */
  appointmentId?: string | null;
  /** Status of the linked appointment, when appointmentId is set. */
  appointmentStatus?: string | null;
}

// Note: BusinessRole is defined in types/role.ts
// This represents roles within a specific business (Manager, Stylist, etc.)

export interface Invitation {
  id: string;
  businessId: string;
  email: string;
  roleIds: string[];
  roleNames?: string[];
  status: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
  token: string;
  expiresAt: string;
  businessName?: string;
  acceptedAt?: string;
  acceptedBy?: string;
  readAt?: string; // When the notification was read
  createdAt: string;
  updatedAt: string;
}

export interface Notification {
  id: string;
  business_id: string;
  sender_id?: string; // Can be null for system notifications
  receiver_id: string;
  type:
    | 'INVITATION_ACCEPTED'
    | 'APPOINTMENT_BOOKED'
    | 'APPOINTMENT_CANCELLED'
    | 'APPOINTMENT_RESCHEDULED'
    | 'QUOTE_REQUEST_SUBMITTED'
    | 'QUOTE_BUSINESS_MESSAGE'
    | 'QUOTE_OFFER_RECEIVED'
    | 'QUOTE_OFFER_ACCEPTED'
    | 'TESTIMONIAL_SUBMITTED'
    | 'GIFT_CARD_REDEEMED'
    | 'ROLE_ASSIGNED'
    | 'ROLE_REMOVED'
    | 'SERVICE_ADDED'
    | 'SERVICE_UPDATED'
    | 'SYSTEM_ANNOUNCEMENT'
    | 'INVOICE_SENT'
    | 'INVOICE_PAID'
    | 'INVOICE_PAYMENT_RECEIVED'
    | 'APPOINTMENT_DEPOSIT_RECEIVED'
    | 'APPOINTMENT_DEPOSIT_REQUESTED';
  category?: 'APPOINTMENT' | 'REVIEW' | 'SERVICE' | 'ALERT';
  title: string;
  message: string;
  metadata?: any; // Additional data specific to the notification type
  is_read: boolean;
  read_at?: string;
  created_at: string;
  updated_at: string;
}

export interface InvitationFormData {
  email: string;
  roleIds: string[];
  inviterName?: string;
  canPerformServices?: boolean;
}

export interface EmailCheckResponse {
  exists: boolean;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface Testimonial {
  id: string;
  businessId: string;
  customerId: string;
  appointmentId?: string;
  serviceId?: string;
  content: string;
  rating: number; // 1-5 stars
  title?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectionReason?: string;
  moderatedBy?: string;
  moderatedAt?: string;
  isPublic: boolean;
  isFeatured: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  // Related data
  customer?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  moderator?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  appointment?: {
    id: string;
    scheduledAt: string;
    service?: {
      name: string;
    };
  };
  service?: {
    name: string;
  };
  business?: {
    name: string;
  };
}

export interface BusinessRating {
  id: string;
  businessId: string;
  customerId: string;
  appointmentId: string;
  content: string;
  rating: number; // 1-5 stars
  title?: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | string;
  rejectionReason?: string | null;
  moderatedBy?: string | null;
  moderatedAt?: string | null;
  isPublic: boolean;
  isFeatured: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  replies?: ReviewReply[]; // One-level replies (no nesting used in UI)
  // Optional joins (used in business dashboard testimonials view)
  customer?: {
    firstName: string;
    lastName: string;
    email?: string;
  };
  appointment?: {
    id: string;
    scheduledAt: string;
  };
}

export interface BusinessRatingFormData {
  appointmentId: string;
  rating: number;
  content?: string;
  title?: string;
}

export interface TestimonialFormData {
  content: string;
  rating: number;
  title?: string;
  appointmentId?: string;
  serviceId?: string;
}

export interface TestimonialModerationData {
  status: 'APPROVED' | 'REJECTED';
  rejectionReason?: string;
  isPublic?: boolean;
  isFeatured?: boolean;
  displayOrder?: number;
}

export interface Professional {
  id: string;
  name: string;
  email: string;
  phone?: string;
  isActive: boolean;
  canPerformServices: boolean;
  joinedAt: string;
  role?: string;
  roleIds?: string[];
  roleDescription?: string;
  roleProtection?: {
    isProtected: boolean;
    reason?: string;
    canEdit: boolean;
    canDeactivate: boolean;
    canDelete: boolean;
  };
}

export interface AvailableProfessional {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  canPerformServices: boolean;
  availability: {
    isAvailable: boolean;
    timeSlots: string[];
  };
}

export interface ReviewReply {
  id: string;
  userId: string;
  userName: string;
  userRole: 'professional' | 'customer' | 'owner';
  message: string;
  createdAt: string;
  updatedAt: string;
  replies?: ReviewReply[]; // Nested replies
}

export interface ServiceReview {
  id: string;
  appointmentId: string;
  serviceId: string;
  professionalId?: string;
  customerId: string;
  rating: number; // 1-5 star rating
  reviewFeedback?: string; // Optional text review
  isPublic: boolean;
  isFeatured: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  replies?: ReviewReply[]; // Array of replies (can be nested)
  // Related data
  service?: {
    name: string;
    description?: string;
  };
  professional?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  customer?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  appointment?: {
    id: string;
    scheduledAt: string;
    businessName?: string;
  };
}

export interface ServiceReviewFormData {
  appointmentId: string;
  serviceId: string;
  professionalId?: string;
  rating: number;
  reviewFeedback?: string;
}

export interface ServiceReviewStats {
  totalReviews: number;
  averageRating: number;
  ratingDistribution: {
    [key: number]: number; // rating -> count
  };
  totalRatings: number;
}
